import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';

// The portal handles image uploads to these paths, so we prepend its host.
const PORTAL_BASE_URL = process.env.REACT_APP_PORTAL_BASE_URL || 'http://localhost:8000';
const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${PORTAL_BASE_URL}/${path.replace(/^\/+/, '')}`;
};

function Albums() {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
        const ALBUMS_API_URL = process.env.REACT_APP_PORTAL_BASE_URL
            ? `${process.env.REACT_APP_PORTAL_BASE_URL}/api/site/albums`
            : 'https://portal.petrochoice.ae/api/site/albums';

        axios
            .get(ALBUMS_API_URL)
            .then((res) => {
                if (res.data && res.data.success) {
                    setAlbums(res.data.data || []);
                } else {
                    setAlbums(res.data || []);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching albums:', err.message);
                setError('Failed to load albums');
                setLoading(false);
            });
    }, []);

    const truncateText = (text, maxLen = 100) => {
        if (!text) return '';
        if (text.length <= maxLen) return text;
        return text.slice(0, maxLen).trim() + '...';
    };

    return (
        <div style={styles.page}>
            {/* Header Section */}
            <div style={styles.headerSection} data-aos="fade-up">
                <div style={styles.headerContent}>
                    <h1 style={styles.pageTitle}>Photo Gallery</h1>
                    <p style={styles.pageSubtitle}>Explore our memorable moments inside Petrochoice.</p>
                    <div style={styles.titleAccent}></div>
                </div>
            </div>

            <div style={styles.container}>
                {loading && (
                    <div style={styles.loaderWrap}>
                        <div style={styles.spinner}></div>
                        <p style={{ color: '#6b7280', marginTop: '16px' }}>Loading albums...</p>
                    </div>
                )}

                {error && <div className="alert alert-danger">{error}</div>}

                {!loading && !error && albums.length === 0 && (
                    <div style={styles.emptyState} data-aos="fade-up">
                        <div style={{ fontSize: '80px', color: '#e2e8f0', marginBottom: '20px' }}>
                            <i className="fas fa-images"></i>
                        </div>
                        <h3 style={{ color: '#0a3161', fontWeight: 'bold' }}>No Albums Available</h3>
                        <p style={{ color: '#6b7280', fontSize: '16px', maxWidth: '500px', margin: '0 auto 24px' }}>
                            There are currently no photo albums in the gallery. New memories will be uploaded soon, so stay tuned!
                        </p>
                        <Link to="/" style={styles.returnBtn}>
                            Return to Home
                        </Link>
                    </div>
                )}

                <div style={styles.albumGrid}>
                    {albums.map((album, idx) => (
                        <Link
                            key={album.id}
                            to={`/albums/${album.id}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                            data-aos="fade-up"
                            data-aos-delay={Math.min(idx * 100, 400)}
                        >
                            <div
                                style={styles.albumCard}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.12)';
                                    const overlay = e.currentTarget.querySelector('.card-overlay');
                                    if (overlay) overlay.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)';
                                    const overlay = e.currentTarget.querySelector('.card-overlay');
                                    if (overlay) overlay.style.opacity = '0';
                                }}
                            >
                                {/* Image Container */}
                                <div style={styles.imageContainer}>
                                    <img
                                        src={album.cover_image_url ? getImageUrl(album.cover_image_url) : '/assets/about us im 01.jpeg'}
                                        alt={album.title}
                                        style={styles.albumImage}
                                    />
                                    <div className="card-overlay" style={styles.cardOverlay}>
                                        <span style={styles.viewAlbumBtn}>
                                            <i className="fas fa-images" style={{ marginRight: '8px' }}></i>
                                            View Album
                                        </span>
                                    </div>
                                    {/* Photo count badge */}
                                    <div style={styles.photoBadge}>
                                        <i className="fas fa-camera" style={{ marginRight: '6px', fontSize: '11px' }}></i>
                                        {album.images?.length || 0}
                                    </div>
                                </div>

                                {/* Card Body */}
                                <div style={styles.cardBody}>
                                    <h3 style={styles.albumTitle}>{album.title}</h3>
                                    {album.description && (
                                        <p style={styles.albumDesc}>
                                            {truncateText(album.description, 100)}
                                        </p>
                                    )}
                                    <div style={styles.cardFooter}>
                                        <span style={styles.exploreLink}>
                                            Explore Album <i className="fas fa-arrow-right" style={{ marginLeft: '6px', fontSize: '12px' }}></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '80vh',
        backgroundColor: '#f8fafc',
        paddingBottom: '60px',
    },
    headerSection: {
        paddingTop: '30px',
        paddingBottom: '10px',
        textAlign: 'center',
    },
    headerContent: {
        maxWidth: '700px',
        margin: '0 auto',
        padding: '0 20px',
    },
    pageTitle: {
        color: '#1565c0',
        fontWeight: '800',
        fontSize: 'clamp(28px, 4vw, 40px)',
        marginBottom: '10px',
    },
    pageSubtitle: {
        color: '#6b7280',
        fontSize: '17px',
        marginBottom: '16px',
    },
    titleAccent: {
        width: '60px',
        height: '4px',
        backgroundColor: '#f77c00',
        margin: '0 auto',
        borderRadius: '2px',
    },
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
    },
    loaderWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40vh',
    },
    spinner: {
        width: '48px',
        height: '48px',
        border: '4px solid #e2e8f0',
        borderTopColor: '#f77c00',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        backgroundColor: '#fff',
        borderRadius: '20px',
        border: '1px dashed #e2e8f0',
    },
    returnBtn: {
        display: 'inline-block',
        padding: '10px 28px',
        backgroundColor: '#f77c00',
        color: '#fff',
        borderRadius: '25px',
        textDecoration: 'none',
        fontWeight: '600',
        fontSize: '15px',
    },
    albumGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: '28px',
        marginTop: '20px',
    },
    albumCard: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.35s ease, box-shadow 0.35s ease',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    imageContainer: {
        position: 'relative',
        height: '240px',
        overflow: 'hidden',
    },
    albumImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.5s ease',
    },
    cardOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(21, 101, 192, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.35s ease',
    },
    viewAlbumBtn: {
        color: '#fff',
        fontSize: '16px',
        fontWeight: '600',
        padding: '12px 28px',
        border: '2px solid #fff',
        borderRadius: '30px',
        letterSpacing: '0.5px',
    },
    photoBadge: {
        position: 'absolute',
        top: '14px',
        right: '14px',
        backgroundColor: 'rgba(0,0,0,0.6)',
        color: '#fff',
        fontSize: '13px',
        fontWeight: '600',
        padding: '5px 12px',
        borderRadius: '20px',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
    },
    cardBody: {
        padding: '20px 24px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    albumTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '8px',
    },
    albumDesc: {
        fontSize: '14px',
        color: '#64748b',
        lineHeight: '1.6',
        marginBottom: '12px',
        flex: 1,
    },
    cardFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingTop: '8px',
        borderTop: '1px solid #f1f5f9',
    },
    exploreLink: {
        color: '#f77c00',
        fontWeight: '600',
        fontSize: '14px',
        display: 'inline-flex',
        alignItems: 'center',
    },
};

export default Albums;
